import { Component, Input, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';

interface Sparkle {
  id: string;
  x: string;
  y: string;
  color: string;
  delay: number;
  scale: number;
  lifespan: number;
  opacity: number;
  rotation: number;
  scaleValue: number;
}

@Component({
  selector: 'nc-sparkles-text',
  templateUrl: './sparkles-text.component.html',
  styleUrls: ['./sparkles-text.component.less']
})
export class SparklesTextComponent implements OnDestroy, OnChanges {
  /** 展示的文本内容 */
  @Input() ncText: string = '';
  /** 闪烁点的数量 */
  @Input() ncCount: number = 10;
  /** 闪烁点的颜色数组 */
  @Input() ncColors: string[] = ['#9E7AFF', '#FE8BBB', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];
  /** 闪烁循环时间（秒） */
  @Input() ncDuration: number = 0.8;
  /** 闪烁点的最大尺寸 */
  @Input() ncSize: number = 21;

  sparkles: Sparkle[] = [];
  private intervalId?: number;
  private animationFrameId?: number;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ncText']) {
      this.cleanup();
      this.initializeSparkles();
      this.startAnimation();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private initializeSparkles(): void {
    this.sparkles = Array.from({ length: this.ncCount }, () => this.generateSparkle());
  }

  private generateSparkle(): Sparkle {
    const x = `${Math.random() * 100}%`;
    const y = `${Math.random() * 100}%`;
    const color = this.ncColors[Math.floor(Math.random() * this.ncColors.length)];
    const delay = Math.random() * 2;
    const scale = Math.random() * 1 + 0.3;
    const lifespan = Math.random() * 10 + 5;
    const id = `${x}-${y}-${Date.now()}-${Math.random()}`;
    const opacity = 0;
    const rotation = 75;
    const scaleValue = 0;

    return { id, x, y, color, delay, scale, lifespan, opacity, rotation, scaleValue };
  }

  private startAnimation(): void {
    // 更新闪烁点生命周期
    this.intervalId = window.setInterval(() => {
      this.sparkles = this.sparkles.map(sparkle => {
        if (sparkle.lifespan <= 0) {
          return this.generateSparkle();
        } else {
          return { ...sparkle, lifespan: sparkle.lifespan - 0.1 };
        }
      });
    }, 100);

    // 动画循环 - 模拟Framer Motion的关键帧动画
    const animate = () => {
      this.sparkles = this.sparkles.map(sparkle => {
        const time = (Date.now() * 0.001 + sparkle.delay) % this.ncDuration; // 使用可配置的循环时间
        const progress = time / this.ncDuration;

        // 模拟关键帧动画 [0, 1, 0]
        let opacity: number;
        let scaleValue: number;
        let rotation: number;

        if (progress <= 0.5) {
          // 前半段：0 -> 1
          const t = progress * 2;
          opacity = t;
          scaleValue = t * sparkle.scale;
          rotation = 75 + (120 - 75) * t;
        } else {
          // 后半段：1 -> 0
          const t = (progress - 0.5) * 2;
          opacity = 1 - t;
          scaleValue = (1 - t) * sparkle.scale;
          rotation = 120 + (150 - 120) * t;
        }

        return { ...sparkle, opacity, rotation, scaleValue };
      });

      this.animationFrameId = requestAnimationFrame(animate);
    };

    animate();
  }

  private cleanup(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  getSparkleStyle(sparkle: Sparkle): any {
    return {
      left: sparkle.x,
      top: sparkle.y,
      opacity: sparkle.opacity,
      transform: `scale(${sparkle.scaleValue}) rotate(${sparkle.rotation}deg)`
    };
  }

  trackBySparkleId(index: number, sparkle: Sparkle): string {
    return sparkle.id;
  }
}
